// ---------------------------------------------------------------------------
// Content dictionary infrastructure for page-level i18n
// ---------------------------------------------------------------------------
// Each page has a content file exporting Record<SupportedLocale, PageContent>.
// Components call usePageContent() to get content for the active locale.
// All content is statically imported — instant switching, no async.
// ---------------------------------------------------------------------------

import { useLocaleStore } from '../locale.store';
import { useLocaleContext } from '../locale.context';
import type { LocaleContentMap } from '../i18n.config';

/**
 * Hook that selects the correct locale entry from a content record.
 * Falls back to English if the locale key is missing.
 *
 * Priority: LocaleContext (SSR-safe) > Zustand store > 'en' default
 *
 * Usage:
 *   import { HOME_CONTENT } from '@/core/i18n/content/home.content';
 *   const c = usePageContent(HOME_CONTENT);
 *   // c.hero.title => translated string
 */
export function usePageContent<T>(content: LocaleContentMap<T>): T {
  const contextLocale = useLocaleContext();
  const storeLocale = useLocaleStore((s) => s.locale);
  const locale = contextLocale ?? storeLocale;
  return content[locale] ?? content.en;
}

export type { LocaleContentMap };
export type { SupportedLocale } from '../i18n.config';
